import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';
import {
  useLazyRegionsListQuery,
  useUpdateCorporateProfileMutation,
} from '../../store/builder/inspections.api';
import {setLoader} from '../../store/builder/reducer';
import {toast} from 'react-toastify';
import Wrapper from '../../components/Wrapper';
import {Helmet} from 'react-helmet';
import DashboardHeader from '../../components/DashboardHeader';
import Cropper from 'react-cropper';
import Profile1 from '../../SVG/Profile1';
import Input from '../../components/Input';
import Checkbox from '../../components/Checkbox';
import CorporateRegions from '../../components/Corporate/CorporateRegions';
import History from '../../SVG/History';
import CorporateRegionDetails from '../../components/Corporate/CorporateRegionDetails';
import CorrectiveActionsIcon from '../../SVG/CorrectiveActions';
import CorporateCorrectiveActions from '../../components/Corporate/CorporateCorrectiveActions';
import CorporateUsers from '../../components/Corporate/CorporateUsers';
import {validationEmail} from '../../global/validate';
import {useTranslation} from '../../hooks/useTranslation';

const CorporateSettings = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const [currentTab, setCurrentTab] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [chosenPhoto, setChosenPhoto] = useState(null);
  const [cropper, setCropper] = useState();
  const [chosenRegionId, setChosenRegionId] = useState(null);
  const userInfo = useSelector(state => state?.rootReducer?.loginSlice?.user);
  const changeTab = tabId => setCurrentTab(tabId);
  const [currentRegion, setCurrentRegion] = useState(null);
  const [corporateProfile, setCorporateProfile] = useState({
    title: null,
    phone: null,
    email: null,
    timezone: null,
    start_day_minutes: null,
    logo: null,
    has_upstream_tests: false,
    daily_weekly_grace_period: null,
    has_corrected_dp: false,
    notification_time_for_due_inspections: null,
    inactivity_timeout_mobile: 0,
    inactivity_timeout_web: 0,
    is_pin_enabled: false,
    pin_require_seconds: null,
  });

  const [regionsList, {data: regionsListData, isLoading: regionsListLoading}] =
    useLazyRegionsListQuery();

  const [
    updateSettings,
    {isLoading: updateSettingsLoading, isSuccess: updateSettingsIsSuccess},
  ] = useUpdateCorporateProfileMutation();

  useEffect(() => {
    regionsList();
  }, []);

  useEffect(() => {
    if (userInfo?.corporate) {
      setCorporateProfile({
        title: userInfo.corporate.title,
        phone: userInfo.corporate.phone,
        email: userInfo.corporate.email,
        logo: userInfo.corporate.logo,
        start_day_minutes: userInfo.corporate.start_day_minutes,
        has_upstream_tests: userInfo.corporate.has_upstream_tests
          ? true
          : false,
        has_corrected_dp: userInfo.corporate.has_corrected_dp ? true : false,
        daily_weekly_grace_period:
          userInfo?.corporate?.daily_weekly_grace_period,
        has_hydrant_systems: userInfo?.corporate?.has_hydrant_systems,
        notification_time_for_due_inspections:
          userInfo?.corporate?.notification_time_for_due_inspections,
        inactivity_timeout_mobile:
          userInfo?.corporate?.inactivity_timeout_mobile ?? 0,
        inactivity_timeout_web:
          userInfo?.corporate?.inactivity_timeout_web ?? 0,
        is_pin_enabled: !!userInfo?.corporate?.is_pin_enabled,
        pin_require_seconds: userInfo?.corporate?.pin_require_seconds ?? null,
      });
    }
  }, [regionsListLoading]);

  useEffect(() => {
    if (location?.search) {
      setCurrentTab(Number(location?.search?.split('=')[1]));
    }
  }, []);

  useEffect(() => {
    if (typeof chosenPhoto === 'string') {
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  }, [chosenPhoto]);

  useEffect(() => {
    if (updateSettingsLoading) {
      dispatch(setLoader(true));
    } else {
      dispatch(setLoader(false));
    }

    if (updateSettingsIsSuccess) {
      dispatch(setLoader(false));
      toast.success(t('corporateSettings.messages.successfullyUpdated'));
      setTimeout(() => getUserIndex(), 100);
    }
  }, [updateSettingsLoading, updateSettingsIsSuccess]);

  const cropperHandler = e => {
    e.preventDefault();
    let files;
    if (e.dataTransfer) {
      files = e.dataTransfer.files;
    } else if (e.target) {
      files = e.target.files;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setChosenPhoto(reader.result);
    };
    reader.readAsDataURL(files[0]);
  };

  function base64ToBlob(base64Data) {
    const contentType = base64Data.substring(5, base64Data.indexOf(';'));
    const byteCharacters = atob(
      base64Data.substring(base64Data.indexOf(',') + 1),
    );
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
      const slice = byteCharacters.slice(offset, offset + 1024);
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, {type: contentType});
  }

  const getCropData = () => {
    if (typeof cropper !== 'undefined') {
      const form = new FormData();
      form.append(
        'profile_photo',
        base64ToBlob(cropper.getCroppedCanvas().toDataURL()),
      );
      setCorporateProfile(old => ({
        ...old,
        logo: base64ToBlob(cropper.getCroppedCanvas().toDataURL()),
      }));
      setTimeout(() => {
        setShowModal(false);
      }, 200);
    }
  };

  const getRegionsList = () => {
    regionsList();
  };

  const handleCorporate = e => {
    setCorporateProfile(old => ({...old, [e.target.name]: e.target.value}));
  };
  const handleCheckbox = e => {
    setCorporateProfile(old => ({
      ...old,
      has_upstream_tests: e.target.checked === true ? 1 : 0,
    }));
  };
  const handleCheckboxCorrectedDp = e => {
    setCorporateProfile(old => ({
      ...old,
      has_corrected_dp: e.target.checked === true ? 1 : 0,
    }));
  };

  const handleCheckboxHasHydrantSystems = e => {
    setCorporateProfile(old => ({
      ...old,
      has_hydrant_systems: e.target.checked === true ? 1 : 0,
    }));
  };

  const handleCheckboxIsPinEnabled = e => {
    setCorporateProfile(old => ({...old, is_pin_enabled: e.target.checked}));
  };

  // The PIN has to be asked before the session is dropped, so it must stay
  // under the mobile timeout. The PIN delay is in seconds while the timeout is
  // in minutes; a mobile timeout of 0 never expires, so there is nothing to
  // compare against.
  const pinRequireSeconds = Number(corporateProfile?.pin_require_seconds);
  const mobileTimeout = Number(corporateProfile?.inactivity_timeout_mobile);
  const pinRequireSecondsError = !corporateProfile?.is_pin_enabled
    ? null
    : !corporateProfile?.pin_require_seconds || !(pinRequireSeconds >= 1)
      ? t('modal.pinRequireSecondsRequired')
      : mobileTimeout > 0 && pinRequireSeconds >= mobileTimeout * 60
        ? t('modal.pinRequireSecondsLessThanMobileTimeout')
        : null;

  const update = () => {
    if (pinRequireSecondsError) {
      toast.error(pinRequireSecondsError);
      return;
    }
    if (
      corporateProfile?.title?.length !== 0 &&
      validationEmail(corporateProfile?.email) &&
      corporateProfile?.start_day_minutes !== null
    ) {
      const form = new FormData();
      if (chosenPhoto) {
        form.append(
          'logo',
          base64ToBlob(cropper.getCroppedCanvas().toDataURL()),
        );
      }
      form.append('title', corporateProfile.title);
      form.append(
        'daily_weekly_grace_period',
        corporateProfile?.daily_weekly_grace_period,
      );
      form.append('email', corporateProfile.email);
      form.append(
        'has_upstream_tests',
        corporateProfile.has_upstream_tests ? 1 : 0,
      );
      form.append(
        'has_corrected_dp',
        corporateProfile.has_corrected_dp ? 1 : 0,
      );
      form.append(
        'notification_time_for_due_inspections',
        corporateProfile.notification_time_for_due_inspections,
      );
      form.append(
        'has_hydrant_systems',
        corporateProfile.has_hydrant_systems ? 1 : 0,
      );
      form.append(
        'inactivity_timeout_mobile',
        corporateProfile.inactivity_timeout_mobile ?? 0,
      );
      form.append(
        'inactivity_timeout_web',
        corporateProfile.inactivity_timeout_web ?? 0,
      );
      form.append('is_pin_enabled', corporateProfile.is_pin_enabled ? 1 : 0);
      if (corporateProfile.is_pin_enabled) {
        form.append('pin_require_seconds', corporateProfile.pin_require_seconds ?? '');
      }

      updateSettings(form);
    } else {
      toast.error(t('corporateSettings.messages.fillAllFieldsCorrectly'));
    }
  };

  return (
    <Wrapper>
      <Helmet>
        <title>{t('corporateSettings.pageTitle')}</title>
      </Helmet>
      <DashboardHeader title={t('corporateSettings.title')} />
      {showModal && (
        <div className='p-modal'>
          <div className='p-modal__content'>
            <div className='p-modal__header'>
              <p></p>
              <h5 className='p-modal__title'>
                {t('corporateSettings.modals.cropImage.title')}
              </h5>
              <div className='p-modal__close'>
                <img
                  src='/assets/icons/close.svg'
                  alt='close'
                  onClick={() => {
                    setShowModal(false);
                    setChosenPhoto(null);
                  }}
                />
              </div>
            </div>
            <div className='p-modal__cropper'>
              <Cropper
                style={{
                  height: 400,
                  width: '100%',
                }}
                zoomTo={0}
                initialAspectRatio={1}
                preview='.img-preview'
                src={chosenPhoto}
                aspectRatio={1}
                viewMode={0}
                minCropBoxHeight={300}
                minCropBoxWidth={300}
                background={false}
                responsive={true}
                autoCropArea={1}
                checkOrientation={false}
                onInitialized={instance => {
                  setCropper(instance);
                }}
                guides={true}
              />
            </div>
            <div className='p-modal__btns'>
              <label
                htmlFor='profile_photo'
                className='profile__imageBox-upload'
              >
                {t('corporateSettings.modals.cropImage.changePhoto')}
              </label>
              <div className='profile__imageBox-upload' onClick={getCropData}>
                {t('corporateSettings.modals.cropImage.savePhoto')}
              </div>
            </div>
          </div>
          <div
            className='p-modal__bg'
            onClick={() => {
              setShowModal(false);
              setChosenPhoto(null);
            }}
          ></div>
        </div>
      )}
      {showDeleteModal && (
        <div className='modal'>
          <div className='modal__content'>
            <div className='modal__content-head'>
              <p>{t('corporateSettings.modals.deleteCard.title')}</p>
              <img
                className='modal__content-exit'
                src='/assets/icons/close.svg'
                alt='close'
                onClick={() => setShowDeleteModal(false)}
              />
            </div>
            <div
              className='modal__content-body'
              style={{display: 'flex', justifyContent: 'center'}}
            >
              {t('corporateSettings.modals.deleteCard.message')}
            </div>
            <div className='modal__content-bottom'>
              <button
                className='close'
                onClick={() => setShowDeleteModal(false)}
              >
                {t('corporateSettings.modals.deleteCard.cancel')}
              </button>
              <button className='save danger'>
                {t('corporateSettings.modals.deleteCard.delete')}
              </button>
            </div>
          </div>
          <div
            className='modal__bg'
            onClick={() => setShowDeleteModal(false)}
          ></div>
        </div>
      )}
      <div className='profile'>
        <div className='profile__tabs'>
          <div
            className={`profile__tab ${currentTab === 0 ? 'active' : ''}`}
            onClick={() => {
              changeTab(0);
            }}
          >
            <Profile1 active={currentTab === 0 && true} />
            <p className='profile__tab-name'>
              {t('corporateSettings.tabs.corporateProfile')}
            </p>
          </div>
          {!userInfo?.is_regional_corporate && (
            <div
              className={`profile__tab ${currentTab === 1 ? 'active' : ''}`}
              onClick={() => {
                changeTab(1);
              }}
            >
              <CorrectiveActionsIcon active={currentTab === 1 && true} />
              <p className='profile__tab-name'>
                {t('corporateSettings.tabs.correctiveActionSettings')}
              </p>
            </div>
          )}

          {!userInfo?.is_regional_corporate && (
            <div
              className={`profile__tab ${currentTab === 5 || currentTab === 6 ? 'active' : ''}`}
              onClick={() => {
                changeTab(5);
              }}
            >
              <History
                active={(currentTab === 5 || currentTab === 6) && true}
              />
              <p className='profile__tab-name'>
                {t('corporateSettings.tabs.regions')}
              </p>
            </div>
          )}

          {!userInfo?.is_regional_corporate && (
            <div
              className={`profile__tab ${currentTab === 3 ? 'active' : ''}`}
              onClick={() => {
                changeTab(3);
              }}
            >
              <Profile1 active={currentTab === 3 && true} />
              <p className='profile__tab-name'>
                {t('corporateSettings.tabs.corporateUsers')}
              </p>
            </div>
          )}
        </div>
        <div
          className={`profile__box ${currentTab === 5 || currentTab === 3 || currentTab === 6 ? 'regions' : ''}`}
        >
          {currentTab === 0 ? (
            <div
              style={{pointerEvents: userInfo?.is_sso_auth ? 'none' : 'auto'}}
            >
              <div className='profile__info column'>
                <p className='profile__title'>
                  {t('corporateSettings.corporateProfile.title')}
                </p>
                <div className='profile__imageBox'>
                  <img
                    className='profile__avatar'
                    src={chosenPhoto ? chosenPhoto : userInfo?.corporate?.logo}
                    alt='avatar'
                  />
                  {!userInfo?.is_regional_corporate &&
                    !userInfo?.is_sso_auth && (
                      <div>
                        <p className='profile__imageBox-desc'>
                          {t(
                            'corporateSettings.corporateProfile.uploadDescription',
                          )}
                        </p>
                        <label
                          htmlFor='profile_photo'
                          className='profile__imageBox-label'
                        >
                          <input
                            type='file'
                            name='profile_photo'
                            id='profile_photo'
                            onChange={cropperHandler}
                            accept='image/jpeg, image/png, image/jpg'
                          />
                          {t('corporateSettings.corporateProfile.uploadPhoto')}
                        </label>
                      </div>
                    )}
                </div>
              </div>
              <form className='profile__form'>
                <div className='profile__form-top'>
                  <div className='profile__form-input'>
                    <Input
                      label={t(
                        'corporateSettings.corporateProfile.corporateName',
                      )}
                      placeholder={t(
                        'corporateSettings.corporateProfile.corporateName',
                      )}
                      name={'title'}
                      containerWidth={'350px'}
                      defaultValue={
                        userInfo?.corporate?.title
                          ? userInfo?.corporate?.title
                          : ''
                      }
                      onChange={handleCorporate}
                      disabled={userInfo?.is_regional_corporate}
                    />
                    <Input
                      label={t(
                        'corporateSettings.corporateProfile.corporateEmail',
                      )}
                      placeholder={t(
                        'corporateSettings.corporateProfile.corporateEmail',
                      )}
                      name={'email'}
                      containerWidth={'350px'}
                      defaultValue={
                        userInfo?.corporate?.email
                          ? userInfo?.corporate?.email
                          : ''
                      }
                      onChange={handleCorporate}
                      disabled={userInfo?.is_regional_corporate}
                    />
                    {/*<div className="auth__field phone">*/}
                    {/*  <label htmlFor="fbo_phone" className="auth__label">*/}
                    {/*    FBO Phone*/}
                    {/*  </label>*/}
                    {/*  <span className="phone__num">+1</span>*/}
                    {/*  <input*/}
                    {/*    id="fbo_phone"*/}
                    {/*    placeholder="Enter phone number"*/}
                    {/*    type="tel"*/}
                    {/*    value={phoneNumber}*/}
                    {/*    // pattern="\+\d\s\d{3}\s\d{3}-\d{4}"*/}
                    {/*    className="auth__input corporate"*/}
                    {/*    onChange={handlePhoneChange}*/}
                    {/*  />*/}
                    {/*</div>*/}
                  </div>
                </div>
                <div className='facility-modal__field' style={{marginTop: 16}}>
                  <Checkbox
                    key={'has_upstream_tests'}
                    label={t(
                      'corporateSettings.corporateProfile.hasUpstreamTests',
                    )}
                    id={'has_upstream_tests'}
                    checked={corporateProfile?.has_upstream_tests}
                    onChange={handleCheckbox}
                    disabled={userInfo?.is_regional_corporate}
                  />
                  <Checkbox
                    checked={corporateProfile.has_corrected_dp}
                    key={'has_corrected_dp'}
                    label={t(
                      'corporateSettings.corporateProfile.hasCorrectedDp',
                    )}
                    id={'has_corrected_dp'}
                    onChange={handleCheckboxCorrectedDp}
                    disabled={userInfo?.is_regional_corporate}
                  />
                  <Checkbox
                    checked={corporateProfile?.has_hydrant_systems}
                    key={'has_hydrant_systems'}
                    label={t(
                      'corporateSettings.corporateProfile.hasHydrantSystems',
                    )}
                    id={'has_hydrant_systems'}
                    onChange={handleCheckboxHasHydrantSystems}
                    disabled={userInfo?.is_regional_corporate}
                  />
                </div>
                <div style={{marginTop: 16}}>
                  <Input
                    tooltip={true}
                    tooltipText={t(
                      'corporateSettings.corporateProfile.dailyWeeklyGracePeriodTooltip',
                    )}
                    containerWidth={'350px'}
                    id='daily_weekly_grace_period'
                    name='daily_weekly_grace_period'
                    placeholder={t(
                      'corporateSettings.corporateProfile.dailyWeeklyGracePeriod',
                    )}
                    label={t(
                      'corporateSettings.corporateProfile.dailyWeeklyGracePeriod',
                    )}
                    type={'number'}
                    onChange={handleCorporate}
                    defaultValue={
                      userInfo?.corporate?.daily_weekly_grace_period
                        ? userInfo?.corporate?.daily_weekly_grace_period
                        : ''
                    }
                    disabled={userInfo?.is_regional_corporate}
                  />
                </div>
                {/* Here  */}
                <Input
                  id='inactivity_timeout_mobile'
                  name='inactivity_timeout_mobile'
                  placeholder={t('modal.inactivityTimeoutMobile')}
                  label={t('modal.inactivityTimeoutMobile')}
                  type={'number'}
                  containerWidth={'350px'}
                  onChange={handleCorporate}
                  defaultValue={
                    userInfo?.corporate?.inactivity_timeout_mobile ?? 0
                  }
                  disabled={userInfo?.is_regional_corporate}
                />
                <div style={{marginTop: 16}}>
                  <Checkbox
                    key={'is_pin_enabled'}
                    label={t('modal.isPinEnabled')}
                    id={'is_pin_enabled'}
                    checked={!!corporateProfile?.is_pin_enabled}
                    onChange={handleCheckboxIsPinEnabled}
                    disabled={userInfo?.is_regional_corporate}
                  />
                </div>
                <Input
                  id='pin_require_seconds'
                  name='pin_require_seconds'
                  placeholder={t('modal.pinRequireSeconds')}
                  label={t('modal.pinRequireSeconds')}
                  type={'number'}
                  containerWidth={'350px'}
                  onChange={handleCorporate}
                  value={corporateProfile?.pin_require_seconds ?? ''}
                  disabled={
                    userInfo?.is_regional_corporate ||
                    !corporateProfile?.is_pin_enabled
                  }
                />
                {pinRequireSecondsError && (
                  <span
                    style={{
                      color: '#EB5757',
                      fontSize: 12,
                      marginTop: 4,
                      display: 'block',
                    }}
                  >
                    {pinRequireSecondsError}
                  </span>
                )}
                <Input
                  id='inactivity_timeout_web'
                  name='inactivity_timeout_web'
                  placeholder={t('modal.inactivityTimeoutWeb')}
                  label={t('modal.inactivityTimeoutWeb')}
                  type={'number'}
                  containerWidth={'350px'}
                  onChange={handleCorporate}
                  defaultValue={
                    userInfo?.corporate?.inactivity_timeout_web ?? 0
                  }
                  disabled={userInfo?.is_regional_corporate}
                />
              </form>
              {!userInfo?.is_regional_corporate && !userInfo?.is_sso_auth && (
                <button
                  className='profile__methods-btn corporate'
                  onClick={update}
                >
                  {t('corporateSettings.corporateProfile.save')}
                </button>
              )}
            </div>
          ) : currentTab === 1 ? (
            <CorporateCorrectiveActions />
          ) : currentTab === 3 ? (
            <CorporateUsers />
          ) : currentTab === 5 ? (
            <CorporateRegions
              regions={regionsListData}
              currentRegion={currentRegion}
              setCurrentRegion={setCurrentRegion}
              getRegionsList={getRegionsList}
              setCurrentTab={setCurrentTab}
              setChosenRegionId={setChosenRegionId}
            />
          ) : currentTab === 6 ? (
            <CorporateRegionDetails
              currentRegion={currentRegion}
              setCurrentRegion={setCurrentRegion}
              chosenRegionId={chosenRegionId}
              getRegionsList={getRegionsList}
              setCurrentTab={setCurrentTab}
            />
          ) : null}
        </div>
      </div>
    </Wrapper>
  );
};

export default CorporateSettings;
